import { useMutation, useQueryClient } from '@tanstack/react-query'
import { invalidateQueriesSafe } from '@/api/invalidate-queries-safe'
import { apiClient } from '@/api/http/client'
import { queryKeys } from '@/api/query-keys'
import { useSessionStore } from '@/stores/useSessionStore'

function tenantKey(tenantId: string | null | undefined) {
  return tenantId ?? '_'
}

function invalidateQuickInstrumentLists(
  qc: ReturnType<typeof useQueryClient>,
  tenantId: string | null | undefined,
  instrumentId?: string,
) {
  const tk = tenantKey(tenantId)
  invalidateQueriesSafe(qc, { queryKey: queryKeys.instruments.list(tk) })
  invalidateQueriesSafe(qc, { queryKey: queryKeys.instruments.all(tk) })
  if (instrumentId) {
    invalidateQueriesSafe(qc, {
      queryKey: queryKeys.instruments.detail(tk, instrumentId),
    })
    invalidateQueriesSafe(qc, {
      queryKey: queryKeys.instrumentDashboard.summary(tk, instrumentId),
    })
  }
}

/** Pause a quick (or any) instrument via existing lifecycle API. */
export function usePauseQuickInstrument() {
  const qc = useQueryClient()
  const tenantId = useSessionStore((s) => s.user?.tenantId)

  return useMutation({
    mutationFn: async (instrumentId: string) => {
      await apiClient.post(`/api/v1/instruments/${instrumentId}/pause`)
    },
    onSuccess: (_data, instrumentId) => {
      invalidateQuickInstrumentLists(qc, tenantId, instrumentId)
    },
  })
}

/** Stop a quick instrument via existing lifecycle API. */
export function useStopQuickInstrument() {
  const qc = useQueryClient()
  const tenantId = useSessionStore((s) => s.user?.tenantId)

  return useMutation({
    mutationFn: async (args: {
      instrumentId: string
      stopReason: string
      stopReasonOther?: string | null
    }) => {
      await apiClient.post(`/api/v1/instruments/${args.instrumentId}/stop`, {
        stopReason: args.stopReason,
        stopReasonOther: args.stopReasonOther,
      })
    },
    onSuccess: (_data, { instrumentId }) => {
      invalidateQuickInstrumentLists(qc, tenantId, instrumentId)
    },
  })
}

/** BFP-1129 — local mirror of the reactivate body until shared types land on the CI branch. */
export type ReactivateQuickInstrumentBody = {
  additionalResponseTarget?: number | null
}

export type ReactivateQuickInstrumentArgs = {
  instrumentId: string
  /** BFP-1129 — Sign-up Surveys paused at target; omit to resume without a body. */
  body?: ReactivateQuickInstrumentBody
}

/** Resume / reactivate a paused or stopped instrument. */
export function useReactivateQuickInstrument() {
  const qc = useQueryClient()
  const tenantId = useSessionStore((s) => s.user?.tenantId)

  return useMutation({
    mutationFn: async ({ instrumentId, body }: ReactivateQuickInstrumentArgs) => {
      await apiClient.patch(`/api/v1/instruments/${instrumentId}/reactivate`, body)
    },
    onSuccess: (_data, { instrumentId }) => {
      invalidateQuickInstrumentLists(qc, tenantId, instrumentId)
    },
  })
}
